import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { Play, CheckCircle2, BookOpen, Lock, ClipboardList } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { ModuleDetailResponse } from '@/api/content'
import type { ModuleLearningConfig } from '@/types/configuration'

export interface ModuleOverviewProps {
  moduleDetail: ModuleDetailResponse
  completedLessonIds: number[]
  moduleLearning?: ModuleLearningConfig | null
  moduleActivityStatus?: {
    startActivityCompleted: boolean
    endActivityCompleted: boolean
  }
  learnerName?: string
  onStartActivity: (type: 'start' | 'end') => void
}

export function ModuleOverview({
  moduleDetail,
  completedLessonIds,
  moduleLearning,
  moduleActivityStatus,
  learnerName,
  onStartActivity,
}: ModuleOverviewProps) {
  const { t } = useTranslation('learner')
  const totalLessons = moduleDetail.lessons?.length ?? 0
  const completedLessons = moduleDetail.lessons?.filter((lesson) =>
    completedLessonIds.includes(lesson.id)
  ).length
  const progressPercent = totalLessons > 0
    ? Math.round((completedLessons / totalLessons) * 100)
    : 0

  const hasStart = moduleLearning?.startActivity?.enabled === true
  const hasEnd = moduleLearning?.endActivity?.enabled === true

  // 모든 레슨이 완료되었는지 확인
  const allLessonsCompleted = totalLessons > 0 && completedLessons === totalLessons

  // 퀴즈 옵션 가져오기
  const endQuizOptions = moduleLearning?.endActivity?.quizOptions
  const questionCount = endQuizOptions?.questionCount ?? 10
  const passingScore = endQuizOptions?.passingScore ?? 70

  // 학습자 이름 (없으면 기본값)
  const displayName = learnerName || t('learning.moduleOverview.learner')

  const renderStartActivityCard = () => {
    const completed = moduleActivityStatus?.startActivityCompleted

    return (
      <div className={cn(
        "flex flex-col gap-4 p-6 rounded-2xl border transition-all duration-200",
        completed ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            "flex justify-center items-center w-12 h-12 rounded-xl",
            completed ? "bg-amber-200 text-amber-800" : "bg-amber-50 text-amber-600"
          )}>
            {completed ? <CheckCircle2 className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{t('learning.moduleOverview.startQuiz')}</h3>
            <p className="text-sm text-gray-600">{t('learning.moduleOverview.startQuizDesc')}</p>
          </div>
        </div>
        <button
          onClick={() => onStartActivity('start')}
          className={cn(
            "px-4 py-2 rounded-lg font-medium transition-colors",
            completed
              ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
              : "bg-amber-600 text-white hover:bg-amber-700"
          )}
        >
          {completed ? t('learning.moduleOverview.retake') : t('learning.moduleOverview.start')}
        </button>
      </div>
    )
  }

  const renderEndActivityCard = () => {
    const completed = moduleActivityStatus?.endActivityCompleted
    const isLocked = !allLessonsCompleted

    const handleClick = () => {
      if (isLocked) {
        toast.warning(t('learning.moduleOverview.completeAllLessons'))
        return
      }
      onStartActivity('end')
    }

    return (
      <div className={cn(
        "flex flex-col gap-5 p-6 rounded-2xl border transition-all duration-200",
        isLocked ? "bg-gray-50 border-gray-200 opacity-60" :
        completed ? "bg-emerald-50 border-emerald-200" : "bg-white border-gray-200"
      )}>
        {/* 아이콘 + 타이틀 */}
        <div className="flex items-center gap-3">
          <div className={cn(
            "flex justify-center items-center w-12 h-12 rounded-xl",
            isLocked ? "bg-gray-200 text-gray-400" :
            completed ? "bg-emerald-200 text-emerald-800" : "bg-amber-50 text-amber-600"
          )}>
            {isLocked ? <Lock className="w-6 h-6" /> :
             completed ? <CheckCircle2 className="w-6 h-6" /> : <ClipboardList className="w-6 h-6" />}
          </div>
          <h3 className={cn(
            "text-lg font-bold",
            isLocked ? "text-gray-400" : "text-gray-900"
          )}>
            {t('learning.moduleOverview.endQuizGuide')}
          </h3>
        </div>

        {/* 안내 멘트 */}
        <div className={cn(
          "space-y-2 text-sm leading-relaxed",
          isLocked ? "text-gray-400" : "text-gray-700"
        )}>
          {isLocked ? (
            <p>{t('learning.moduleOverview.lessonsRequired', { completed: completedLessons, total: totalLessons })}</p>
          ) : completed ? (
            <>
              <p>{t('learning.moduleOverview.endQuizPassed', { name: displayName })}</p>
              <p>{t('learning.moduleOverview.canStartNextModule')}</p>
            </>
          ) : (
            <>
              <p>{t('learning.moduleOverview.allLessonsCompleted', { name: displayName })}</p>
              <p>{t('learning.moduleOverview.tryEndQuiz')}</p>
              <p className="text-xs text-gray-500">
                {t('learning.moduleOverview.passingScoreNote')}
              </p>
            </>
          )}
        </div>

        {/* 퀴즈 정보 */}
        {!isLocked && (
          <div className={cn(
            "flex gap-6 py-3 px-4 rounded-lg text-sm",
            completed ? "bg-emerald-100/50" : "bg-gray-50"
          )}>
            <div>
              <span className="text-gray-500">{t('learning.moduleOverview.questionCount')} </span>
              <span className="font-medium text-gray-900">{t('learning.moduleOverview.questionCountValue', { count: questionCount })}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('learning.moduleOverview.targetScore')} </span>
              <span className="font-medium text-gray-900">{t('learning.moduleOverview.targetScoreValue', { score: passingScore })}</span>
            </div>
          </div>
        )}

        {/* 버튼 */}
        <button
          onClick={handleClick}
          disabled={isLocked}
          className={cn(
            "px-4 py-2.5 rounded-lg font-medium transition-colors",
            isLocked
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : completed
                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                : "bg-amber-600 text-white hover:bg-amber-700"
          )}
        >
          {isLocked ? t('learning.moduleOverview.lessonRequired') : completed ? t('learning.moduleOverview.retake') : t('learning.moduleOverview.start')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8 duration-300 animate-in fade-in">
      <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
              {t('learning.moduleOverview.module')}
            </span>
            {totalLessons > 0 && (
              <span className="text-sm text-gray-500">
                {t('learning.moduleOverview.progressStatus', { percent: progressPercent, completed: completedLessons, total: totalLessons })}
              </span>
            )}
          </div>

          <h1 className="flex gap-3 items-center mb-4 text-2xl font-bold leading-tight text-gray-900 md:text-3xl">
            <BookOpen className="w-7 h-7 text-gray-700 md:w-8 md:h-8" />
            <ContentMetadataText text={moduleDetail.bareTitle || moduleDetail.title} className="min-w-0" />
          </h1>

          {moduleDetail.description && (
            <p className="text-lg font-light leading-relaxed text-gray-600">
              <ContentMetadataText text={moduleDetail.description} />
            </p>
          )}
        </div>
      </div>

      {(hasStart || hasEnd) && (
        <div className={cn(
          "grid gap-5",
          hasStart && hasEnd ? "md:grid-cols-2" : "md:grid-cols-1"
        )}>
          {hasStart && renderStartActivityCard()}
          {hasEnd && renderEndActivityCard()}
        </div>
      )}
    </div>
  )
}
